import { MainframeService } from '@amzn/awscat-portfolio-assessment-service-client';
import { StoredEnvironment, StoredMainframe } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Button, Container, Header, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-react';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../../common/Localization';
import ConfirmationModal from '../../common/ConfirmationModal';

type Props = {
    mainframe: StoredMainframe;
    portfolioId: string;
    reloadMainframes: () => void;
} & FlashContextInterface;
const mainframeService = new MainframeService();
const minWidthConst = '140px';

function MainframeEntry({ flash, mainframe, portfolioId, reloadMainframes }: Props): JSX.Element {
    const isMounted = useIsMounted();
    const history = useHistory();
    const redirectToEdit = () => {
        history.push(`/portfolio/${portfolioId}/management/assets/mainframe/edit/${mainframe.id}`);
    };
    const [ deleteFrictionVisible, setDeleteFrictionVisible ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const header = (
        <Header actions={
            <SpaceBetween size='m' direction='horizontal'>
                <Button onClick={() => setDeleteFrictionVisible(true)}>{constants.DELETE_BUTTON_TEXT}</Button>
                <Button onClick={redirectToEdit}>{constants.EDIT_BUTTON_TEXT}</Button>
            </SpaceBetween>
        }>{mainframe.name}</Header>
    );

    const onConfirmDelete = async () => {
        try {
            setLoading(true);
            await mainframeService.delete(portfolioId, { mainframeName: mainframe.name });
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.FAILED_TO_DELETE_MAINFRAME);
            }
        } finally {
            if (isMounted()) {
                setDeleteFrictionVisible(false);
                setLoading(false);
                reloadMainframes();
            }
        }
    };

    const colDefs: TableProps.ColumnDefinition<StoredEnvironment>[] = [
        {
            id: 'name',
            header: constants.NAME,
            cell: item => item.name,
            minWidth: minWidthConst,
        },
        {
            id: 'envType',
            header: constants.ENVIRONMENT,
            cell: item => item.envType,
            minWidth: minWidthConst,
        },
        {
            id: 'oltpHrs',
            header: constants.OLTP_HOURS,
            cell: item => item.oltpHrs,
            minWidth: minWidthConst,
        },
        {
            id: 'batchHrs',
            header: constants.BATCH_HOURS,
            cell: item => item.batchHrs,
            minWidth: minWidthConst,
        },
        {
            id: 'avgOltpMips',
            header: constants.AVG_OLTP_MIPS,
            cell: item => item.avgOltpMips,
            minWidth: minWidthConst,
        },
        {
            id: 'avgBatchMips',
            header: constants.AVG_BATCH_MIPS,
            cell: item => item.avgBatchMips,
            minWidth: minWidthConst,
        },
        {
            id: 'avgDBMipS',
            header: constants.AVG_DATABASE_MIPS,
            cell: item => item.avgDBMips,
            minWidth: minWidthConst,
        },
        {
            id: 'dbDiskUsedTB',
            header: constants.DB_DISK_USED,
            cell: item => item.dbDiskUsedTB,
            minWidth: minWidthConst,
        },
        {
            id: 'filesDiskUsedTB',
            header: constants.FILE_DISK_USED,
            cell: item => item.filesDiskUsedTB,
            minWidth: minWidthConst,
        },
        {
            id: 'backupTapeTB',
            header: constants.BACKUP_TAPE,
            cell: item => item.backupTapeTB,
            minWidth: minWidthConst,
        },
        {
            id: 'backupTapeRetrTB',
            header: constants.BACKUP_TAPE_RETRIEVAL,
            cell: item => item.backupTapeRetrTB,
            minWidth: minWidthConst,
        },
        {
            id: 'hsmAndSmsTapeTB',
            header: constants.HSM_SMS_TAPE,
            cell: item => item.hsmAndSmsTapeTB,
            minWidth: minWidthConst,
        },
        {
            id: 'hsmAndSmsTapeRetrTB',
            header: constants.HSM_SMS_TAPE_RETRIEVAL,
            cell: item => item.hsmAndSmsTapeRetrTB,
            minWidth: minWidthConst,
        },
        {
            id: 'appEnvCount',
            header: constants.APP_ENV_COUNT,
            cell: item => item.appEnvCount,
            minWidth: minWidthConst,
        },
        {
            id: 'dbEnvCount',
            header: constants.DB_ENV_COUNT,
            cell: item => item.dbEnvCount,
            minWidth: minWidthConst,
        },
        {
            id: 'dataTransferOutGB',
            header: constants.DATA_TRANSFER_OUT,
            cell: item => item.dataTransferOutGB,
            minWidth: minWidthConst,
        },
    ];
    return (
        <Container key={mainframe.id} header={header}>
            <Table items={mainframe.environments} columnDefinitions={colDefs}></Table>
            {deleteFrictionVisible ? (
                <ConfirmationModal
                    visible
                    header={constants.CONFIRM_DELETE_MAINFRAME}
                    friction={constants.DELETE_FRICTION}
                    confirmLoading={loading}
                    onConfirm={onConfirmDelete}
                    confirmButton={constants.DELETE_BUTTON_TEXT}
                    onDismiss={() => setDeleteFrictionVisible(false)}
                ></ConfirmationModal>
            )
                : null}
        </Container>
    );
}

export default withFlashContext(MainframeEntry);
